import React, { useState } from "react";
import { ChevronDown, Clock, Mail, MapPin, MessageCircle, Phone, ExternalLink } from "lucide-react";
import { CpImage } from "@/components/company/CpImage";
import { useBranding } from "@/context/BrandingContext";
import { resolveLocation } from "@/lib/companyProfile";
import { SectionHeading } from "@/components/company/CompanySections";

export function GallerySection({ data }) {
  if (!data?.images?.length) return null;
  return (
    <section id="galeri" className="storefront-section cp-anchor" data-testid="company-gallery" aria-labelledby="cp-gallery-title">
      <SectionHeading eyebrow="Galeri" title={data.title} id="cp-gallery-title" />
      <div className="cp-gallery">
        {data.images.map((it, i) => (
          <figure key={`${it.image}-${i}`} className="cp-gallery-item" data-testid={`company-gallery-item-${i}`}>
            <CpImage slot="gallery" src={it.image} alt={it.caption || `Galeri ${i + 1}`} />
            {it.caption ? <figcaption className="cp-gallery-caption">{it.caption}</figcaption> : null}
          </figure>
        ))}
      </div>
    </section>
  );
}

function FaqItem({ item, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="cp-faq-item" data-open={open ? "true" : "false"} data-testid={`company-faq-item-${index}`}>
      <button type="button" className="cp-faq-q" aria-expanded={open} onClick={() => setOpen((v) => !v)} data-testid={`company-faq-toggle-${index}`}>
        <span>{item.question}</span><ChevronDown size={18} className="cp-faq-chevron" aria-hidden="true" />
      </button>
      {open ? <div className="cp-faq-a cp-prose" data-testid={`company-faq-answer-${index}`}>{item.answer}</div> : null}
    </div>
  );
}

export function FaqSection({ data }) {
  if (!data?.items?.length) return null;
  return (
    <section id="faq" className="storefront-section cp-anchor" data-testid="company-faq" aria-labelledby="cp-faq-title">
      <SectionHeading eyebrow="FAQ" title={data.title || "Pertanyaan yang Sering Diajukan"} id="cp-faq-title" />
      <div className="cp-faq">{data.items.map((it, i) => <FaqItem key={`${it.question}-${i}`} item={it} index={i} />)}</div>
    </section>
  );
}

export function LocationSection({ data }) {
  const { branding } = useBranding();
  const loc = resolveLocation(data, branding?.contact);
  if (!loc) return null;
  const rows = [
    loc.address && { icon: MapPin, label: "Alamat", value: loc.address, key: "address" },
    loc.hours && { icon: Clock, label: "Jam Operasional", value: loc.hours, key: "hours" },
    loc.phone && { icon: Phone, label: "Telepon", value: loc.phone, href: `tel:${loc.phone.replace(/[^+\d]/g, "")}`, key: "phone" },
    loc.whatsapp && { icon: MessageCircle, label: "WhatsApp", value: `+${loc.whatsapp}`, href: `https://wa.me/${loc.whatsapp}`, key: "whatsapp" },
    loc.email && { icon: Mail, label: "Email", value: loc.email, href: `mailto:${loc.email}`, key: "email" },
  ].filter(Boolean);
  return (
    <section id="lokasi" className="storefront-section cp-anchor" data-testid="company-location" aria-labelledby="cp-loc-title">
      <SectionHeading eyebrow="Lokasi & Kontak" title={loc.title || "Kunjungi atau Hubungi Kami"} id="cp-loc-title" />
      <div className="cp-location">
        <ul className="cp-location-list">
          {rows.map((r) => {
            const Icon = r.icon;
            return (
              <li key={r.key} className="cp-location-row" data-testid={`company-location-${r.key}`}>
                <span className="cp-location-icon"><Icon size={18} aria-hidden="true" /></span>
                <div className="min-w-0">
                  <div className="cp-location-label">{r.label}</div>
                  {r.href ? <a href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="cp-location-value cp-link">{r.value}</a>
                    : <div className="cp-location-value whitespace-pre-line">{r.value}</div>}
                </div>
              </li>
            );
          })}
        </ul>
        {loc.maps_url ? (
          <a href={loc.maps_url} target="_blank" rel="noopener noreferrer" className="cp-map-card" data-testid="company-location-map">
            <MapPin size={26} aria-hidden="true" />
            <span className="font-bold">Buka di Peta</span>
            <span className="text-sm opacity-80 inline-flex items-center gap-1">Lihat lokasi <ExternalLink size={14} aria-hidden="true" /></span>
          </a>
        ) : null}
      </div>
    </section>
  );
}
